import { gql, useApolloClient, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React from 'react';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

interface Props {
  isOpen: boolean;
  graphId: string;
  membership: GraphQLTypes.RemoveGraphRoleOverrideModalMembershipFragment;
  isSelf: boolean;
  onClose: () => void;
}

export const RemoveGraphRoleOverrideModalMembershipFragment = gql`
  fragment RemoveGraphRoleOverrideModalMembershipFragment on AccountMembership {
    createdAt
    role: permission
    user {
      id
      fullName
    }
    account {
      id
    }
  }
`;

export const RemoveGraphRoleOverrideModal = ({
  isOpen,
  graphId,
  membership,
  isSelf,
  onClose,
}: Props) => {
  const client = useApolloClient();
  const [removeRoleOverride, { data, error, loading }] = useMutation<
    GraphQLTypes.RemoveGraphRoleMutation,
    GraphQLTypes.RemoveGraphRoleMutationVariables
  >(gql`
    mutation RemoveGraphRoleMutation($graphId: ID!, $userID: ID!) {
      service(id: $graphId) {
        overrideUserPermission(userID: $userID, permission: null) {
          id
          roleOverrides {
            role
            user {
              id
            }
          }
        }
      }
    }
  `);

  // call onCancel if the mutation is done and there are no errors / not loading
  // we know the data prop will be populated after the mutation is done, not before
  React.useEffect(() => {
    if (data && !error) {
      onClose();
    }
  }, [data, error, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            Are you sure you want to remove the graph role for{' '}
            {isSelf ? 'yourself' : membership.user.fullName}?
          </ModalTitle>
        </ModalHeader>
        {!!error && (
          <ModalBody>
            <ErrorMessage error={error} />
          </ModalBody>
        )}
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              variant="destructive"
              type="button"
              onClick={async () => {
                await removeRoleOverride({
                  variables: {
                    graphId,
                    userID: membership.user.id,
                  },
                });

                if (isSelf) {
                  await client.resetStore();
                }
              }}
              isLoading={loading}
              loadingText="Yes, remove"
            >
              Yes, remove
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
